/**
 * Zod schemas shared by the API and both frontends, so a contract change breaks
 * typecheck on every side at once rather than at runtime in a browser.
 *
 * Rule 2: the server decides amounts and currency. Amounts therefore arrive
 * here as decimal strings and are converted to `bigint` cents server-side by
 * `centsFromJson`; no client-supplied number is ever trusted as an amount.
 */
import { z } from 'zod';
import { ADMIN_ROLES, ORG_ROLES } from './domain';
import { SETTING_KEYS } from './settings';

/** A non-negative integer count of minor units, transported as a string. */
export const centsSchema = z
  .string()
  .regex(/^\d{1,15}$/, 'amount must be an integer number of cents');

/** A "12.34" major-unit string, converted to cents server-side. */
export const decimalAmountSchema = z
  .string()
  .regex(/^\d{1,13}(?:\.\d{1,2})?$/, 'amount must look like 12 or 12.34');

export const currencySchema = z
  .string()
  .length(3)
  .regex(/^[A-Z]{3}$/, 'currency must be an ISO 4217 code');

export const slugSchema = z
  .string()
  .min(3)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'slug must be lowercase kebab-case');

/** PATCH /settings/:key — the value is validated per key by the Setting service. */
export const updateSettingBodySchema = z.object({
  value: z.unknown(),
  /** Optional human note recorded on the audit row. */
  reason: z.string().min(1).max(500).optional(),
});
export type UpdateSettingBody = z.infer<typeof updateSettingBodySchema>;

/** GET /settings — a key/value map, amounts as strings. */
export const settingsResponseSchema = z.object({
  settings: z.record(z.string(), z.unknown()),
});
export type SettingsResponse = z.infer<typeof settingsResponseSchema>;

export const settingKeySchema = z.enum(SETTING_KEYS as [string, ...string[]]);

export const sessionResponseSchema = z.object({
  sub: z.string(),
  email: z.string().optional(),
  tenantId: z.string().optional(),
  roles: z.array(z.string()).default([]),
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

export const meResponseSchema = z.object({
  donorId: z.string(),
  email: z.string().nullable(),
  adminRoles: z.array(z.enum(ADMIN_ROLES)),
  orgRoles: z.array(
    z.object({
      organizationId: z.string(),
      organizationName: z.string(),
      role: z.enum(ORG_ROLES),
    }),
  ),
});
export type MeResponse = z.infer<typeof meResponseSchema>;
